import { InputTextField, RadioButton } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Dropdown } from '../../molecules/Dropdown';
import { LegalTextArea } from '../../molecules/LegalTextArea';
import { BrgModelProps, FormData } from './BrgClaimForm.types';
import { allowDigitsCommaDot } from '../../utils/inputSanitizers';

interface BrgComparisonRateInfoProps {
  comparisonRateInformation: BrgModelProps['comparisonRateInformation'];
  dropDownPlaceholder: BrgModelProps['dropDownPlaceholder'];
  currencyOptions: Array<{ label: string; value: string }>;
  isCurrencyDataLoading: boolean;
}
export function BrgComparisonRateInfo({
  comparisonRateInformation,
  currencyOptions,
  dropDownPlaceholder,
  isCurrencyDataLoading,
}: BrgComparisonRateInfoProps) {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<FormData>();

  const isWebsiteOrApp = watch('isWebSiteApp');

  const RadioOptions = [
    {
      id: 'radio-website',
      value: comparisonRateInformation.websiteOrApp?.websiteRadioButtonLabel,
      label: comparisonRateInformation.websiteOrApp?.websiteRadioButtonLabel,
    },
    {
      id: 'radio-app',
      value: comparisonRateInformation.websiteOrApp?.appRadioButtonLabel,
      label: comparisonRateInformation.websiteOrApp?.appRadioButtonLabel,
    },
  ];

  const deviceOptions = useMemo(
    () =>
      comparisonRateInformation.deviceType?.devices?.map(device => ({
        label: device,
        value: device,
      })),
    []
  );

  return (
    <div id="comparison-rate-info" className="max-w-brg">
      <h2>{comparisonRateInformation.componentTitle}</h2>
      <p>{comparisonRateInformation.componentDescription}</p>
      <div className="m-form-group">
        <Controller
          name="lowerRateFoundOrReserved"
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            return (
              <InputTextField
                label={comparisonRateInformation.lowerRateFoundOrReserved?.label}
                messageToShow={comparisonRateInformation.lowerRateFoundOrReserved?.errorLabel}
                showErrorMessage={!!errors.lowerRateFoundOrReserved}
                assignInputValue
                inputValue={field.value}
                getInputValue={val => field.onChange(allowDigitsCommaDot(val))}
                inputMaxLength={16}
                className="m-input-field"
              />
            );
          }}
        />
        <Controller
          name="lowerRateFoundCurrency"
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            return (
              <Dropdown
                id={'lowerRateFoundCurrency'}
                name={'lowerRateFoundCurrency'}
                options={currencyOptions}
                placeholder={dropDownPlaceholder}
                label={comparisonRateInformation.lowerRateCurrency?.label}
                errorMessage={
                  errors.lowerRateFoundCurrency ? comparisonRateInformation.lowerRateCurrency?.errorLabel : ''
                }
                value={field.value}
                handleChange={field.onChange}
                className={clsx({
                  'skeleton-loader': isCurrencyDataLoading,
                })}
              />
            );
          }}
        />
        <Controller
          name="addressAppUsed"
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            return (
              <InputTextField
                label={comparisonRateInformation.whereToFindLowerRate?.label}
                inputValue={field.value}
                className="m-input-field"
                messageToShow={comparisonRateInformation.whereToFindLowerRate?.errorLabel}
                showErrorMessage={!!errors.addressAppUsed}
                getInputValue={field.onChange}
              />
            );
          }}
        />
        <Controller
          name="typeOfDeviceAccessed"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => {
            return (
              <Dropdown
                id={'typeOfDeviceAccessed'}
                name={'typeOfDeviceAccessed'}
                options={deviceOptions || []}
                placeholder={dropDownPlaceholder}
                label={comparisonRateInformation.deviceType?.label}
                errorMessage={fieldState.error ? comparisonRateInformation.deviceType?.errorLabel : ''}
                value={field.value}
                handleChange={field.onChange}
              />
            );
          }}
        />
        <div className="mt-n2">
          <p>{comparisonRateInformation.websiteOrApp?.label}</p>
          <div
            className={clsx('row m-input-field', {
              'focus-error-indicator': errors.isWebSiteApp,
            })}
          >
            {RadioOptions.map(option => (
              <Controller
                key={option.id}
                name="isWebSiteApp"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <RadioButton
                    radiobuttonId={option.id}
                    radiobuttonName="isWebSiteApp"
                    className="m-radio-button mr-3"
                    radiobuttonLabel={option.label!}
                    checked={field.value === option.value}
                    onChange={field.onChange}
                    value={option.value!}
                    setTabIndex={0}
                  />
                )}
              />
            ))}
          </div>
          {errors.isWebSiteApp && (
            <div className="m-input-field is-error">
              <span>{comparisonRateInformation.websiteOrApp?.errorLabel}</span>
            </div>
          )}
        </div>

        {isWebsiteOrApp && (
          <Controller
            name="websiteOrAppInfo"
            control={control}
            rules={{ required: true }}
            render={({ field }) => {
              return (
                <InputTextField
                  label={
                    isWebsiteOrApp === comparisonRateInformation.websiteOrApp?.websiteRadioButtonLabel
                      ? comparisonRateInformation.websiteOrAppUrl?.provideWebsiteURLLabel
                      : comparisonRateInformation.websiteOrAppUrl?.provideAppURLLabel
                  }
                  showErrorMessage={!!errors.websiteOrAppInfo}
                  messageToShow={comparisonRateInformation.websiteOrApp?.errorLabel}
                  inputValue={field.value}
                  className="m-input-field"
                  getInputValue={field.onChange}
                />
              );
            }}
          />
        )}
      </div>
      <p>{comparisonRateInformation.claimReviewAssistanceText}</p>
      <p className="t-font-s">{comparisonRateInformation.claimReviewAssistanceExampleText}</p>
      <Controller
        name="comments"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <LegalTextArea
            textareaName="comments"
            className="mt-4"
            textareaId="brg-form"
            textareaLabel={comparisonRateInformation.comments?.label ?? ''}
            isError={!!errors.comments}
            errorMessage={comparisonRateInformation.comments?.errorLabel}
            inputValue={field.value ?? ''} // !NOTE: when we reset the value become undefined and this become controlled to uncontrolled
            getInputValue={field.onChange}
            maxLength={2000}
          />
        )}
      />
    </div>
  );
}
